import type {AudioTrack, SubtitleTrack} from 'bitmovin-player';
import {assign, createMachine} from 'xstate';

type Track = AudioTrack | SubtitleTrack;

type TrackContext = {
  activeTrack: Track | null;
  tracks: Track[];
};

type TrackEvent =
  | {type: 'openPanel'}
  | {type: 'closePanel'}
  | {type: 'addedTrack'; value: Track}
  | {type: 'removedTrack'; value: Track}
  | {type: 'selectedTrack'; value: Track}
  | {type: 'disabledTrack'; value: Track}
  | {type: 'resetTracks'};

const initialContext: TrackContext = {
  activeTrack: null,
  tracks: [],
};

export const trackMachine = createMachine(
  {
    /** @xstate-layout N4IgpgJg5mDOIC5QBcBOBDAxgawMTogkgBUMcBtABgF1FQAHAe1gEtkXGA7OkAD0QCsANgEA6SgBYhADhmUhEiQEYJATgA0IAJ6IAzKomjpu3dKWrKSofv0BfW5rRY8qMAFtGANxJlsVWkggTKzsXDz8CALCogBMMapKwiqqCvKaOggAtLpixibyFhKm0or2jr64sGAANmCYyD7O-jzBbBzcgRFRQqJRAOwxZkrS0jF9o+mIqqpGJhICfUpKfUIq0n1lIE44uBAssOgARrUQpE00LcxtYZ2CC+KJq4vDiyaTWTHLolZCCkox8yslhim22LjgYGQZxwsGagVaoQ6oC6IlE82MMQEyykujG7wBYj6fUoqgGQkx5LWoN8okw1WYkFwjHoYE4AAV0JwanCGFdEeE9JJeqpdH0TCIpDFcRJ3tFKPKVHEAb8JJQNg4tjTmazcHSGRyudUeUE+e0BQhVDFRAkVDJiSscpJ3rohH1vr9xpRjFjLPJqc5RCzOHtOFAAML0qq4XiwZDoBqidAAMwaqAAFAJ5QBKXBgwOskPhyNgY0Is23C25Iorcw2gTxGKyt2ZywSAHAkVWewaziMIjwQJgy4hcvIxCKcT1r2khJ9IoCaTvTIA3SxNQimKSeXyFT+nC04sQYfXJF8RBKeWiHIi9b1wbSeUy7SITKJQnrsbFImi6R77CibVTzLG4xwQXQLyMVQF03T9lgXZ1XXdV0H29C8LyEP982DFhQwjBlj35CtLUnTdpBnbEckXZ8EDfWZHX0FIbV0btbCAA */
    tsTypes: {} as import('./trackMachine.typegen').Typegen0,
    schema: {
      context: {} as TrackContext,
      events: {} as TrackEvent,
    },
    initial: 'closed',
    context: initialContext,
    states: {
      closed: {
        on: {
          openPanel: {
            target: 'open',
          },
        },
      },
      open: {
        on: {
          closePanel: {
            // The interim state debounces the input
            // Otherwise clicking the panel button while the panel is open
            // will cause the panel to close then open again immediately
            target: 'pendingClose',
          },
        },
      },
      pendingClose: {
        after: {
          '500': 'closed',
        },
      },
    },

    id: 'track',

    on: {
      addedTrack: {
        target: '#track',
        internal: true,
        actions: 'addTrack',
      },
      removedTrack: {
        target: '#track',
        internal: true,
        actions: 'removeTrack',
      },
      selectedTrack: {
        target: '#track',
        internal: true,
        actions: 'setActiveTrack',
      },
      disabledTrack: {
        target: '#track',
        internal: true,
        actions: 'disableTrack',
      },
      resetTracks: {
        target: '#track',
        internal: true,
        actions: 'resetTracks',
      },
    },
  },
  {
    actions: {
      addTrack: assign((context, {value}) => ({
        ...context,
        tracks:
          context.tracks.findIndex((el) => el.id === value.id) === -1
            ? context.tracks.concat(value)
            : context.tracks,
      })),
      removeTrack: assign((context, {value}) => ({
        ...context,
        tracks: context.tracks.filter((el) => el.id === value.id),
      })),
      setActiveTrack: assign((context, {value}) => ({
        ...context,
        activeTrack: value,
      })),
      // Checks if the currently active track is the track which was disabled,
      // and if so sets `activeTrack` to null. This allows for disabling a track
      // with the `Off` PanelItem
      disableTrack: assign((context, {value}) => ({
        ...context,
        activeTrack:
          value.id === context.activeTrack?.id ? null : context.activeTrack,
      })),
      resetTracks: assign(() => initialContext),
    },
  }
);
